import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { modules } from "@/components/home/FeaturesGrid";
import { Reveal, Eyebrow } from "@/components/Reveal";
import FinalCta from "@/components/FinalCta";
import { usePageMeta } from "@/utils/pageMeta";
import { trackEvent } from "@/utils/analytics";

const details = {
  "Réservations": ["Clients, participants et formules au même endroit", "Options et réductions", "Paiements rattachés à chaque réservation"],
  "Planning": ["Vue jour ou semaine", "Affectation des véhicules et des équipes", "Disponibilités visibles en un instant"],
  "Caisse & dépenses": ["Encaissements et mouvements de caisse", "Dépenses catégorisées avec justificatifs", "Suivi de trésorerie au quotidien"],
  "Flotte": ["Disponibilité de chaque véhicule ou équipement", "Maintenance et révisions", "Incidents reliés à l'activité"],
  "Équipe": ["Guides, chauffeurs, réception et collaborateurs", "Accès individuels à la plateforme", "Organisation des journées de travail"],
  "Partenaires": ["Hôtels, riads, agences et guides partenaires", "Suivi des commissions", "Historique des apports d'affaires"],
  "Statistiques": ["CA encaissé, réservations et participants", "Dépenses et résultat estimé", "Performances par activité"],
  "Multi-sites": ["Plusieurs établissements, une organisation", "Données séparées par site", "Vue globale et bascule instantanée"],
};

const Features = () => {
  usePageMeta(
    "Fonctionnalités — ActiPilote",
    "Réservations, planning, caisse & dépenses, flotte, équipe, partenaires, statistiques et multi-sites : découvrez les modules de la plateforme ActiPilote."
  );

  return (
    <>
      <section data-testid="features-page-hero" className="relative overflow-hidden pt-28 pb-12 sm:pt-36 sm:pb-16">
        <div className="bg-grid-faint pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <Eyebrow>Fonctionnalités</Eyebrow>
            <h1 className="mt-4 text-balance text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              Huit modules. Une seule plateforme.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              ActiPilote couvre l'intégralité de votre exploitation : de la réservation au résultat,
              en passant par la caisse, la flotte et l'équipe.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-5 md:grid-cols-2">
            {modules.map((m, i) => (
              <Reveal key={m.title} delay={0.05 * i}>
                <div data-testid={`features-page-card-${i}`} className="group h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-brand/25 hover:shadow-lift">
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                      <m.icon className="h-5 w-5" />
                    </span>
                    <h2 className="text-xl font-semibold text-navy">{m.title}</h2>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-navy/60">{m.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {details[m.title].map((d) => (
                      <li key={d} className="flex items-start gap-2.5 text-sm font-medium text-navy/70">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-turquoise" /> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14 text-center">
            <Link to="/contact" onClick={() => trackEvent("click_demo", { location: "features_page" })} data-testid="features-page-cta" className="group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white shadow-glow transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-700">
              Demander une démo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <FinalCta />
    </>
  );
};

export default Features;
